// Archetype Matrix — scoring (AM1) + Shadow Work bridge (AM2). Pure + deterministic.
//
// AM1: Core Desire Likert (7-point) → 0–100 per archetype → ranked archetypes +
//      family lean. Forced-choice picks are a TIEBREAKER only (ipsative — never
//      mixed into the 0–100 score; plan §5).
// AM2: map the user's Shadow Work per-spectrum result → one of the 5 matrix levels
//      (plan §6), positioning the dominant archetype's 10×5 matrix row-for-row. A
//      coarse fallback maps an inline orientation+integration read when there is no
//      Shadow Work result.

import {
  ARCHETYPES,
  archetypeById,
  ARCHETYPE_FAMILIES,
  MATRIX_ROWS,
  type ArchetypeFamily,
} from '../data/archetypes';
import type { ShadowMap } from './shadowScoring';
import type { ShadowResultType } from '../data/shadowSpectra';

/** Desire scores within this band are a tie → forced-choice picks break it. */
export const ARCHETYPE_TIE_EPS = 2;
/** A shadow-dominant spectrum with shadow-load ≥ this reads as Possession, else Leakage. */
export const ARCHETYPE_POSSESSION_SHADOW_LOAD = 55;
/** The dominant archetype's shadow is "active" when this many rows sit at Leakage/Possession. */
export const ARCHETYPE_ACTIVE_SHADOW_ROWS = 3;

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const round = (n: number) => Math.round(n);
const mean = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0);
/** Mean of 1–7 Likert answers → 0–100. */
const normalize7 = (m: number) => clamp(((m - 1) / 6) * 100, 0, 100);

// ── AM1 — Core Desire → ranked archetypes ─────────────────────────────────────

/** One answered Likert item (1–7), keyed to the archetype whose desire it measures. */
export interface DesireCell {
  archetypeId: string;
  value: number;
}
/** One forced-choice selection → the archetype id(s) it maps to. */
export interface ForcedChoicePick {
  archetypeIds: string[];
}

export interface ArchetypeScore {
  id: string;
  lightName: string;
  shadowName: string;
  family: ArchetypeFamily;
  desire: string;
  score: number; // 0–100 desire strength (PRIMARY)
  fcBonus: number; // forced-choice picks (TIEBREAKER only)
}

export interface FamilyLean {
  family: ArchetypeFamily;
  name: string;
  score: number; // 0–100, mean of the family's archetype desire scores
}

export interface ArchetypeProfile {
  scores: ArchetypeScore[]; // all 12, ranked (dominant first)
  dominant: ArchetypeScore;
  secondary: ArchetypeScore[]; // ranks 2–3
  disowned: ArchetypeScore[]; // lowest 3 desire scores
  families: FamilyLean[]; // ranked
}

/** Build the ranked desire profile from the Likert cells + forced-choice picks. */
export function buildArchetypeProfile(desireCells: DesireCell[], fcPicks: ForcedChoicePick[] = []): ArchetypeProfile {
  const byArch = new Map<string, number[]>();
  for (const c of desireCells) {
    if (!Number.isFinite(c.value)) continue;
    if (!byArch.has(c.archetypeId)) byArch.set(c.archetypeId, []);
    byArch.get(c.archetypeId)!.push(c.value);
  }
  const fcCount = new Map<string, number>();
  for (const p of fcPicks) for (const id of p.archetypeIds) fcCount.set(id, (fcCount.get(id) ?? 0) + 1);

  const scores: ArchetypeScore[] = ARCHETYPES.map(a => ({
    id: a.id,
    lightName: a.lightName,
    shadowName: a.shadowName,
    family: a.family,
    desire: a.desire,
    score: round(normalize7(mean(byArch.get(a.id) ?? []))),
    fcBonus: fcCount.get(a.id) ?? 0,
  }));

  // Primary = desire score; forced-choice only breaks near-ties (plan §5).
  const ranked = [...scores].sort((x, y) => {
    if (Math.abs(y.score - x.score) >= ARCHETYPE_TIE_EPS) return y.score - x.score;
    if (y.fcBonus !== x.fcBonus) return y.fcBonus - x.fcBonus;
    return archetypeById[x.id].order - archetypeById[y.id].order;
  });

  const families: FamilyLean[] = (Object.keys(ARCHETYPE_FAMILIES) as ArchetypeFamily[])
    .map(f => ({
      family: f,
      name: ARCHETYPE_FAMILIES[f].name,
      score: round(mean(scores.filter(s => s.family === f).map(s => s.score))),
    }))
    .sort((a, b) => b.score - a.score);

  const disowned = [...ranked].reverse().slice(0, 3);

  return { scores: ranked, dominant: ranked[0], secondary: ranked.slice(1, 3), disowned, families };
}

// ── AM2 — Shadow Work → matrix-level bridge (plan §6) ─────────────────────────

/** Shadow Work per-spectrum result type → one of the 5 matrix levels (0–4), or
 *  null when the spectrum reads as "low access" (not yet expressed → greyed). */
export function matrixLevelForResultType(resultType: ShadowResultType, shadowLoad: number): number | null {
  switch (resultType) {
    case 'integrated_flexible':
      return 0; // Integrated Light
    case 'healthy_left_leaning':
    case 'healthy_right_leaning':
      return 1; // Healthy Persona
    case 'repressed_counterpart':
      return 2; // Over-Identified Persona
    case 'left_shadow_dominant':
    case 'right_shadow_dominant':
      return shadowLoad >= ARCHETYPE_POSSESSION_SHADOW_LOAD ? 4 : 3; // Possession vs Leakage
    case 'inner_conflict':
      return 4; // Shadow Possession
    case 'low_access':
    default:
      return null; // greyed — not yet expressed
  }
}

export interface MatrixPosition {
  spectrumId: string;
  poles: string;
  level: number | null; // 0–4, or null (low access / no data)
  cell: string; // the archetype's matrix cell text at this level ('' when null)
  resultType?: ShadowResultType;
  resultLabel?: string;
}

/** Where the matrix levels came from: a full Shadow Work result, a coarse inline
 *  quick read, or nothing yet (reference-only). */
export type MatrixSource = 'shadow' | 'coarse' | 'none';

export interface ArchetypeMatrixRead {
  archetypeId: string;
  positions: MatrixPosition[]; // 10 rows in MATRIX_ROWS order
  hasShadowData: boolean; // true only for a full Shadow Work source
  source: MatrixSource;
  integratedRows: number; // rows at Integrated Light (level 0)
  shadowRows: number; // rows at Leakage/Possession (level ≥ 3)
  possessionRows: number; // rows at Shadow Possession (level 4)
  overallLevel: number | null; // mean of the placed levels (lower = more Light)
}

/** Roll placed positions into the read's summary counts (shared by both sources). */
function summarizeMatrix(
  archetypeId: string,
  positions: MatrixPosition[],
  source: MatrixSource,
): ArchetypeMatrixRead {
  const placed = positions.filter((p): p is MatrixPosition & { level: number } => p.level != null);
  return {
    archetypeId,
    positions,
    source: placed.length ? source : 'none',
    hasShadowData: source === 'shadow' && placed.length > 0,
    integratedRows: placed.filter(p => p.level === 0).length,
    shadowRows: placed.filter(p => p.level >= 3).length,
    possessionRows: placed.filter(p => p.level === 4).length,
    overallLevel: placed.length ? Math.round((placed.reduce((s, p) => s + p.level, 0) / placed.length) * 10) / 10 : null,
  };
}

/**
 * Position the dominant archetype's 10 matrix rows using the user's Shadow Work
 * per-spectrum result. With no Shadow Work map, every level is null → the matrix
 * renders as reference-only (plan §6).
 */
export function buildArchetypeMatrixRead(archetypeId: string, shadowMap: ShadowMap | null): ArchetypeMatrixRead {
  const arch = archetypeById[archetypeId];
  const byId = new Map((shadowMap?.spectra ?? []).map(s => [s.spectrumId, s]));

  const positions: MatrixPosition[] = MATRIX_ROWS.map(row => {
    const sr = byId.get(row.spectrumId);
    const level = sr ? matrixLevelForResultType(sr.resultType, sr.shadowLoad) : null;
    const cell = arch && level != null ? arch.matrix[row.spectrumId]?.[level] ?? '' : '';
    return { spectrumId: row.spectrumId, poles: row.poles, level, cell, resultType: sr?.resultType, resultLabel: sr?.resultLabel };
  });

  return summarizeMatrix(archetypeId, positions, shadowMap ? 'shadow' : 'none');
}

/** The dominant archetype's Shadow is "active" when several rows sit in shadow.
 *  Only a full Shadow Work source is precise enough to make this claim. */
export function isShadowActive(read: ArchetypeMatrixRead): boolean {
  return read.source === 'shadow' && read.shadowRows >= ARCHETYPE_ACTIVE_SHADOW_ROWS;
}

/** The full persisted `archetype_profile` payload (results view + AI read from it). */
export interface ArchetypeProfilePayload {
  profile: ArchetypeProfile;
  dominantId: string;
  matrix: ArchetypeMatrixRead; // the dominant archetype's positioned matrix
  hasShadowData: boolean;
  shadowActive: boolean;
}

// ── AM2 — coarse inline fallback (no Shadow Work result) ──────────────────────
// A short inline pass (2 items/spectrum: orientation + integration) gives a
// COARSER read than a full Shadow Work result. This maps that read to a matrix
// level; the UI still prompts the user to take the full Shadow Work for precision.

export type CoarseOrientation = 'left' | 'balanced' | 'right';
export type CoarseIntegration = 'low' | 'mid' | 'high';

export function matrixLevelFromCoarse(integration: CoarseIntegration, orientation: CoarseOrientation): number | null {
  if (integration === 'high') return 0; // both poles available → Integrated Light
  if (integration === 'mid') return 1; // functional style → Healthy Persona
  // low integration: a strong lean leaks (Leakage); a stuck middle over-identifies.
  return orientation === 'balanced' ? 2 : 3;
}

/** One coarse per-spectrum read from the inline quick-read pass. */
export interface CoarseCell {
  spectrumId: string;
  orientation: CoarseOrientation;
  integration: CoarseIntegration;
}

/**
 * Position the dominant archetype's matrix from a COARSE inline read (2 items per
 * spectrum) rather than a full Shadow Work result. Same shape as
 * buildArchetypeMatrixRead, but source = 'coarse' → the UI labels it a quick read
 * and still nudges toward the full Shadow Work for precision.
 */
export function buildArchetypeMatrixReadFromCoarse(archetypeId: string, cells: CoarseCell[]): ArchetypeMatrixRead {
  const arch = archetypeById[archetypeId];
  const byId = new Map(cells.map(c => [c.spectrumId, c]));

  const positions: MatrixPosition[] = MATRIX_ROWS.map(row => {
    const c = byId.get(row.spectrumId);
    const level = c ? matrixLevelFromCoarse(c.integration, c.orientation) : null;
    const cell = arch && level != null ? arch.matrix[row.spectrumId]?.[level] ?? '' : '';
    return { spectrumId: row.spectrumId, poles: row.poles, level, cell };
  });

  return summarizeMatrix(archetypeId, positions, 'coarse');
}
